/**
 * Executive Producer Quips - Data
 *
 * Pool of i18n translation keys for producer quips
 * Actual translations are in packages/shared/src/i18n/locales/en/content.json
 *
 * Theme breakdown:
 * - General quips (80): Pitching (10), Network (10), Budget (10), Talent (10), Trends (10), Meta (10), User-Focused (20)
 * - Context-aware dogfooding quips (200): Family (50), Work (50), Friends (50), Dating (50)
 * - Conflict scenario quips (253): Conflict (23), Escalation (24), Anxious (25), Boundary (27), Avoidant (26), Gaslighting (26), Codependency (27), Power (28), Defensiveness (27), Withdrawal (20)
 *
 * Total: 533 quips
 *
 * INTERNAL USE ONLY - Not re-exported from barrel
 * Part of the 3-file split refactoring (2026-01-07)
 */

import type { QuipEntry } from './types';

export const PRODUCER_QUIPS: QuipEntry[] = [
  // === GENERAL QUIPS (80) - Context-agnostic ===

  // Pitching & Development Hell (10)
  { key: 'producer_quips.pitching_01' },
  { key: 'producer_quips.pitching_02' },
  { key: 'producer_quips.pitching_03' },
  { key: 'producer_quips.pitching_04' },
  { key: 'producer_quips.pitching_05' },
  { key: 'producer_quips.pitching_06' },
  { key: 'producer_quips.pitching_07' },
  { key: 'producer_quips.pitching_08' },
  { key: 'producer_quips.pitching_09' },
  { key: 'producer_quips.pitching_10' },

  // Network/Studio Notes (10)
  { key: 'producer_quips.network_01' },
  { key: 'producer_quips.network_02' },
  { key: 'producer_quips.network_03' },
  { key: 'producer_quips.network_04' },
  { key: 'producer_quips.network_05' },
  { key: 'producer_quips.network_06' },
  { key: 'producer_quips.network_07' },
  { key: 'producer_quips.network_08' },
  { key: 'producer_quips.network_09' },
  { key: 'producer_quips.network_10' },

  // Budget & Production Realities (10)
  { key: 'producer_quips.budget_01' },
  { key: 'producer_quips.budget_02' },
  { key: 'producer_quips.budget_03' },
  { key: 'producer_quips.budget_04' },
  { key: 'producer_quips.budget_05' },
  { key: 'producer_quips.budget_06' },
  { key: 'producer_quips.budget_07' },
  { key: 'producer_quips.budget_08' },
  { key: 'producer_quips.budget_09' },
  { key: 'producer_quips.budget_10' },

  // Managing Talent & Crew (10)
  { key: 'producer_quips.talent_01' },
  { key: 'producer_quips.talent_02' },
  { key: 'producer_quips.talent_03' },
  { key: 'producer_quips.talent_04' },
  { key: 'producer_quips.talent_05' },
  { key: 'producer_quips.talent_06' },
  { key: 'producer_quips.talent_07' },
  { key: 'producer_quips.talent_08' },
  { key: 'producer_quips.talent_09' },
  { key: 'producer_quips.talent_10' },

  // Industry Trends (10)
  { key: 'producer_quips.trends_01' },
  { key: 'producer_quips.trends_02' },
  { key: 'producer_quips.trends_03' },
  { key: 'producer_quips.trends_04' },
  { key: 'producer_quips.trends_05' },
  { key: 'producer_quips.trends_06' },
  { key: 'producer_quips.trends_07' },
  { key: 'producer_quips.trends_08' },
  { key: 'producer_quips.trends_09' },
  { key: 'producer_quips.trends_10' },

  // Self-Aware/Meta (10)
  { key: 'producer_quips.meta_01' },
  { key: 'producer_quips.meta_02' },
  { key: 'producer_quips.meta_03' },
  { key: 'producer_quips.meta_04' },
  { key: 'producer_quips.meta_05' },
  { key: 'producer_quips.meta_06' },
  { key: 'producer_quips.meta_07' },
  { key: 'producer_quips.meta_08' },
  { key: 'producer_quips.meta_09' },
  { key: 'producer_quips.meta_10' },

  // User-Focused Quips (20) - V3: Roleplay context enhancement
  { key: 'producer_quips.user_focused_01' },
  { key: 'producer_quips.user_focused_02' },
  { key: 'producer_quips.user_focused_03' },
  { key: 'producer_quips.user_focused_04' },
  { key: 'producer_quips.user_focused_05' },
  { key: 'producer_quips.user_focused_06' },
  { key: 'producer_quips.user_focused_07' },
  { key: 'producer_quips.user_focused_08' },
  { key: 'producer_quips.user_focused_09' },
  { key: 'producer_quips.user_focused_10' },
  { key: 'producer_quips.user_focused_11' },
  { key: 'producer_quips.user_focused_12' },
  { key: 'producer_quips.user_focused_13' },
  { key: 'producer_quips.user_focused_14' },
  { key: 'producer_quips.user_focused_15' },
  { key: 'producer_quips.user_focused_16' },
  { key: 'producer_quips.user_focused_17' },
  { key: 'producer_quips.user_focused_18' },
  { key: 'producer_quips.user_focused_19' },
  { key: 'producer_quips.user_focused_20' },

  // === CONTEXT-AWARE DOGFOODING QUIPS (200) - V7 FINAL ===
  // Expanded from 20 → 60 → 120 → 200 quips (50 per context: family, work, friends, dating)
  // Removed 'social' context - those scenarios now map to 'other' which falls back to general quips

  // Family Context (50)
  { key: 'producer_quips.dogfooding_family_01', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_02', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_03', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_04', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_05', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_06', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_07', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_08', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_09', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_10', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_11', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_12', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_13', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_14', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_15', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_16', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_17', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_18', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_19', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_20', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_21', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_22', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_23', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_24', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_25', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_26', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_27', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_28', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_29', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_30', contexts: ['family'], isDogfooding: true },

  // Work Context (30)
  { key: 'producer_quips.dogfooding_work_01', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_02', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_03', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_04', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_05', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_06', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_07', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_08', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_09', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_10', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_11', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_12', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_13', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_14', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_15', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_16', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_17', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_18', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_19', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_20', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_21', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_22', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_23', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_24', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_25', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_26', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_27', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_28', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_29', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_30', contexts: ['work'], isDogfooding: true },

  // Friends Context (30)
  { key: 'producer_quips.dogfooding_friends_01', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_02', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_03', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_04', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_05', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_06', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_07', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_08', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_09', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_10', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_11', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_12', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_13', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_14', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_15', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_16', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_17', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_18', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_19', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_20', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_21', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_22', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_23', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_24', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_25', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_26', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_27', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_28', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_29', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_30', contexts: ['friends'], isDogfooding: true },

  // Dating Context (30)
  { key: 'producer_quips.dogfooding_dating_01', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_02', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_03', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_04', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_05', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_06', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_07', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_08', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_09', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_10', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_11', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_12', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_13', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_14', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_15', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_16', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_17', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_18', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_19', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_20', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_21', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_22', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_23', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_24', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_25', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_26', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_27', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_28', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_29', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_30', contexts: ['dating'], isDogfooding: true },

  // Family Context (31-50) - V7 Expansion
  { key: 'producer_quips.dogfooding_family_31', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_32', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_33', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_34', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_35', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_36', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_37', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_38', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_39', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_40', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_41', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_42', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_43', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_44', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_45', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_46', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_47', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_48', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_49', contexts: ['family'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_family_50', contexts: ['family'], isDogfooding: true },

  // Work Context (31-50) - V7 Expansion
  { key: 'producer_quips.dogfooding_work_31', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_32', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_33', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_34', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_35', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_36', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_37', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_38', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_39', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_40', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_41', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_42', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_43', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_44', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_45', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_46', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_47', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_48', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_49', contexts: ['work'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_work_50', contexts: ['work'], isDogfooding: true },

  // Friends Context (31-50) - V7 Expansion
  { key: 'producer_quips.dogfooding_friends_31', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_32', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_33', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_34', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_35', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_36', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_37', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_38', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_39', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_40', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_41', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_42', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_43', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_44', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_45', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_46', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_47', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_48', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_49', contexts: ['friends'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_friends_50', contexts: ['friends'], isDogfooding: true },

  // Dating Context (31-50) - V7 Expansion
  { key: 'producer_quips.dogfooding_dating_31', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_32', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_33', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_34', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_35', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_36', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_37', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_38', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_39', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_40', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_41', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_42', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_43', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_44', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_45', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_46', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_47', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_48', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_49', contexts: ['dating'], isDogfooding: true },
  { key: 'producer_quips.dogfooding_dating_50', contexts: ['dating'], isDogfooding: true },

  // === CONFLICT SCENARIO QUIPS (253) - V8 EXPANSION ===
  // General quips (no context targeting) covering 10 conflict pattern categories
  // Sourced from content.json namespace (2025-01-13)

  // Conflict Patterns (23) - Subject changing, sarcasm, vague answers, dramatic sighs
  { key: 'producer_quips.conflict_subject_changing_01' },
  { key: 'producer_quips.conflict_sarcasm_subtext_01' },
  { key: 'producer_quips.conflict_talk_later_pickup_01' },
  { key: 'producer_quips.conflict_vague_answers_01' },
  { key: 'producer_quips.conflict_dramatic_sigh_01' },
  { key: 'producer_quips.conflict_forgetting_continuity_01' },
  { key: 'producer_quips.conflict_confessional_different_01' },
  { key: 'producer_quips.conflict_busy_b_plot_01' },
  { key: 'producer_quips.conflict_notes_arthouse_01' },
  { key: 'producer_quips.conflict_mean_jokes_01' },
  { key: 'producer_quips.conflict_resentful_fine_01' },
  { key: 'producer_quips.conflict_development_hell_01' },
  { key: 'producer_quips.conflict_audio_issues_01' },
  { key: 'producer_quips.conflict_blocking_opposite_01' },
  { key: 'producer_quips.conflict_no_conflict_mandate_01' },
  { key: 'producer_quips.conflict_meme_vfx_01' },
  { key: 'producer_quips.conflict_insincere_adr_01' },
  { key: 'producer_quips.conflict_director_rogue_01' },
  { key: 'producer_quips.conflict_body_language_01' },
  { key: 'producer_quips.conflict_product_placement_01' },
  { key: 'producer_quips.conflict_period_drama_shift_01' },
  { key: 'producer_quips.conflict_fatigue_crutch_01' },

  // Escalation Tactics (24) - Kitchen sink, villain edit, ultimatums, competing monologues
  { key: 'producer_quips.escalation_kitchen_sink_01' },
  { key: 'producer_quips.escalation_villain_edit_01' },
  { key: 'producer_quips.escalation_audio_peaking_01' },
  { key: 'producer_quips.escalation_practical_effects_01' },
  { key: 'producer_quips.escalation_clip_show_marathon_01' },
  { key: 'producer_quips.escalation_refusing_cut_01' },
  { key: 'producer_quips.escalation_cancellation_tease_01' },
  { key: 'producer_quips.escalation_victim_competition_01' },
  { key: 'producer_quips.escalation_parody_delivery_01' },
  { key: 'producer_quips.escalation_ultimatum_power_01' },
  { key: 'producer_quips.escalation_tracking_shot_01' },
  { key: 'producer_quips.escalation_inciting_incident_01' },
  { key: 'producer_quips.escalation_cornered_blocking_01' },
  { key: 'producer_quips.escalation_competing_monologues_01' },
  { key: 'producer_quips.escalation_false_motivation_01' },
  { key: 'producer_quips.escalation_fade_to_black_01' },
  { key: 'producer_quips.escalation_extras_chaos_01' },
  { key: 'producer_quips.escalation_running_lines_01' },
  { key: 'producer_quips.escalation_apology_rewrite_01' },
  { key: 'producer_quips.escalation_weaponized_backstory_01' },
  { key: 'producer_quips.escalation_score_overpowering_01' },
  { key: 'producer_quips.escalation_conflicting_accounts_01' },
  { key: 'producer_quips.escalation_text_graphics_01' },

  // Anxious Attachment (25) - Social stalking, disaster plots, repeat lines, forensic analysis
  { key: 'producer_quips.anxious_social_stalking_01' },
  { key: 'producer_quips.anxious_texting_pattern_01' },
  { key: 'producer_quips.anxious_are_we_okay_01' },
  { key: 'producer_quips.anxious_perfect_text_01' },
  { key: 'producer_quips.anxious_disaster_plots_01' },
  { key: 'producer_quips.anxious_day_players_jealousy_01' },
  { key: 'producer_quips.anxious_cleared_schedule_01' },
  { key: 'producer_quips.anxious_love_interest_trope_01' },
  { key: 'producer_quips.anxious_sorry_loop_01' },
  { key: 'producer_quips.anxious_recast_terror_01' },
  { key: 'producer_quips.anxious_bottle_episode_01' },
  { key: 'producer_quips.anxious_market_research_01' },
  { key: 'producer_quips.anxious_lighting_dependent_01' },
  { key: 'producer_quips.anxious_avoiding_conflict_01' },
  { key: 'producer_quips.anxious_repeat_lines_01' },
  { key: 'producer_quips.anxious_season_renewed_01' },
  { key: 'producer_quips.anxious_forensic_analysis_01' },
  { key: 'producer_quips.anxious_dialogue_ratio_01' },
  { key: 'producer_quips.anxious_character_rewrite_01' },
  { key: 'producer_quips.anxious_tone_shift_01' },
  { key: 'producer_quips.anxious_live_feed_01' },
  { key: 'producer_quips.anxious_silence_tension_01' },
  { key: 'producer_quips.anxious_sizzle_reels_01' },
  { key: 'producer_quips.anxious_too_big_performance_01' },

  // Boundary Violations (27) - Hijacked episodes, emotional blackmail, uncredited notes
  { key: 'producer_quips.boundary_hijacked_episode_01' },
  { key: 'producer_quips.boundary_uncredited_notes_01' },
  { key: 'producer_quips.boundary_reading_script_01' },
  { key: 'producer_quips.boundary_all_hours_01' },
  { key: 'producer_quips.boundary_exhaustion_delivery_01' },
  { key: 'producer_quips.boundary_dangling_loan_01' },
  { key: 'producer_quips.boundary_on_call_burnout_01' },
  { key: 'producer_quips.boundary_surprise_character_01' },
  { key: 'producer_quips.boundary_booking_without_checking_01' },
  { key: 'producer_quips.boundary_miscast_therapist_01' },
  { key: 'producer_quips.boundary_pg_rating_01' },
  { key: 'producer_quips.boundary_day_rate_same_01' },
  { key: 'producer_quips.boundary_roast_hit_piece_01' },
  { key: 'producer_quips.boundary_off_camera_scenes_01' },
  { key: 'producer_quips.boundary_props_moved_01' },
  { key: 'producer_quips.boundary_emotional_blackmail_01' },
  { key: 'producer_quips.boundary_cut_mid_sentence_01' },
  { key: 'producer_quips.boundary_extreme_closeup_01' },
  { key: 'producer_quips.boundary_documentary_invasive_01' },
  { key: 'producer_quips.boundary_missing_props_01' },
  { key: 'producer_quips.boundary_boom_mic_hovering_01' },
  { key: 'producer_quips.boundary_silent_observer_01' },
  { key: 'producer_quips.boundary_both_sides_dialogue_01' },

  // Avoidant Patterns (26) - Not a texter, vague finales, written out, exit clauses
  { key: 'producer_quips.avoidant_not_big_texter_01' },
  { key: 'producer_quips.avoidant_vague_finale_01' },
  { key: 'producer_quips.avoidant_unreachable_between_takes_01' },
  { key: 'producer_quips.avoidant_one_script_time_01' },
  { key: 'producer_quips.avoidant_pilot_never_picked_01' },
  { key: 'producer_quips.avoidant_beloved_previous_01' },
  { key: 'producer_quips.avoidant_less_depth_note_01' },
  { key: 'producer_quips.avoidant_wide_angle_cut_01' },
  { key: 'producer_quips.avoidant_subplot_entire_show_01' },
  { key: 'producer_quips.avoidant_improv_oneliner_01' },
  { key: 'producer_quips.avoidant_romance_to_drama_01' },
  { key: 'producer_quips.avoidant_written_out_mystery_01' },
  { key: 'producer_quips.avoidant_insignificant_details_01' },
  { key: 'producer_quips.avoidant_one_person_masquerade_01' },
  { key: 'producer_quips.avoidant_hollow_performance_01' },
  { key: 'producer_quips.avoidant_retooled_show_01' },
  { key: 'producer_quips.avoidant_no_promo_joint_01' },
  { key: 'producer_quips.avoidant_never_ensemble_01' },
  { key: 'producer_quips.avoidant_exit_clause_01' },
  { key: 'producer_quips.avoidant_narrated_documentary_01' },
  { key: 'producer_quips.avoidant_cut_for_time_01' },
  { key: 'producer_quips.avoidant_conflicting_notes_01' },
  { key: 'producer_quips.avoidant_bottle_episode_budget_01' },
  { key: 'producer_quips.avoidant_redacted_backstory_01' },
  { key: 'producer_quips.avoidant_recurring_guest_01' },
  { key: 'producer_quips.avoidant_walks_off_emotion_01' },

  // Gaslighting (26) - Overnight rewrites, selective crops, genre swaps, clever edits
  { key: 'producer_quips.gaslighting_overnight_rewrite_01' },
  { key: 'producer_quips.gaslighting_selective_crop_01' },
  { key: 'producer_quips.gaslighting_genre_swap_01' },
  { key: 'producer_quips.gaslighting_test_audience_01' },
  { key: 'producer_quips.gaslighting_stunt_rehearsal_01' },
  { key: 'producer_quips.gaslighting_penciled_clause_01' },
  { key: 'producer_quips.gaslighting_meters_peaked_01' },
  { key: 'producer_quips.gaslighting_confession_montage_01' },
  { key: 'producer_quips.gaslighting_props_continuity_01' },
  { key: 'producer_quips.gaslighting_satire_punchline_01' },
  { key: 'producer_quips.gaslighting_gas_leak_01' },
  { key: 'producer_quips.gaslighting_offscreen_narrator_01' },
  { key: 'producer_quips.gaslighting_check_slate_01' },
  { key: 'producer_quips.gaslighting_splice_points_01' },
  { key: 'producer_quips.gaslighting_on_camera_stereo_01' },
  { key: 'producer_quips.gaslighting_lighting_shook_01' },
  { key: 'producer_quips.gaslighting_mirror_reversed_01' },
  { key: 'producer_quips.gaslighting_choreo_tripwire_01' },
  { key: 'producer_quips.gaslighting_choice_not_cue_01' },
  { key: 'producer_quips.gaslighting_ripped_spine_01' },
  { key: 'producer_quips.gaslighting_sweeping_generalization_01' },
  { key: 'producer_quips.gaslighting_commentary_missing_01' },
  { key: 'producer_quips.gaslighting_press_kit_spin_01' },
  { key: 'producer_quips.gaslighting_bad_dub_01' },
  { key: 'producer_quips.gaslighting_wide_shot_context_01' },
  { key: 'producer_quips.gaslighting_details_truth_01' },

  // Codependency (27) - Merged bios, shared logins, echo tracks, sizzle reels on fire
  { key: 'producer_quips.codependency_craft_services_01' },
  { key: 'producer_quips.codependency_standby_personality_01' },
  { key: 'producer_quips.codependency_merged_bio_01' },
  { key: 'producer_quips.codependency_live_tweeting_01' },
  { key: 'producer_quips.codependency_solo_coverage_01' },
  { key: 'producer_quips.codependency_two_for_one_01' },
  { key: 'producer_quips.codependency_emotional_adr_01' },
  { key: 'producer_quips.codependency_sizzle_fire_01' },
  { key: 'producer_quips.codependency_budget_spinoff_01' },
  { key: 'producer_quips.codependency_show_therapist_01' },
  { key: 'producer_quips.codependency_both_table_read_01' },
  { key: 'producer_quips.codependency_closed_set_01' },
  { key: 'producer_quips.codependency_security_violations_01' },
  { key: 'producer_quips.codependency_note_cancellation_01' },
  { key: 'producer_quips.codependency_committee_approval_01' },
  { key: 'producer_quips.codependency_separation_chemistry_01' },
  { key: 'producer_quips.codependency_fixing_post_01' },
  { key: 'producer_quips.codependency_echo_track_01' },
  { key: 'producer_quips.codependency_house_style_01' },
  { key: 'producer_quips.codependency_first_person_plural_01' },
  { key: 'producer_quips.codependency_spinoff_calendar_01' },
  { key: 'producer_quips.codependency_solo_b_plot_terror_01' },
  { key: 'producer_quips.codependency_group_monologue_01' },
  { key: 'producer_quips.codependency_hostage_thriller_01' },
  { key: 'producer_quips.codependency_pr_interference_01' },
  { key: 'producer_quips.codependency_no_personal_lease_01' },
  { key: 'producer_quips.codependency_light_meter_tied_01' },

  // Power Dynamics (28) - Studio ledger, access lists, surveillance, GPS chyrons
  { key: 'producer_quips.power_studio_ledger_01' },
  { key: 'producer_quips.power_booked_hijack_01' },
  { key: 'producer_quips.power_access_list_01' },
  { key: 'producer_quips.power_rush_golden_01' },
  { key: 'producer_quips.power_emotional_paywall_01' },
  { key: 'producer_quips.power_weaponized_jargon_01' },
  { key: 'producer_quips.power_single_signon_01' },
  { key: 'producer_quips.power_breakout_fan_01' },
  { key: 'producer_quips.power_gps_chyron_01' },
  { key: 'producer_quips.power_paternal_cut_01' },
  { key: 'producer_quips.power_spreadsheet_locked_01' },
  { key: 'producer_quips.power_pyro_budget_01' },
  { key: 'producer_quips.power_sponsor_ad_01' },
  { key: 'producer_quips.power_security_cam_01' },
  { key: 'producer_quips.power_styling_erase_01' },
  { key: 'producer_quips.power_one_hot_mic_01' },
  { key: 'producer_quips.power_day_rate_entitlement_01' },
  { key: 'producer_quips.power_renewal_reshoots_01' },
  { key: 'producer_quips.power_iou_ledger_01' },
  { key: 'producer_quips.power_intimacy_tbd_01' },
  { key: 'producer_quips.power_after_credits_01' },
  { key: 'producer_quips.power_b_roll_locations_01' },
  { key: 'producer_quips.power_expert_comic_relief_01' },
  { key: 'producer_quips.power_funding_yanked_01' },
  { key: 'producer_quips.power_refusing_ensemble_01' },
  { key: 'producer_quips.power_media_training_01' },
  { key: 'producer_quips.power_gravity_neutral_01' },

  // Defensiveness (27) - Tone policing, weaponized recaps, exposition dumps, sloppy wipes
  { key: 'producer_quips.defensiveness_tone_police_01' },
  { key: 'producer_quips.defensiveness_closing_arguments_01' },
  { key: 'producer_quips.defensiveness_weaponized_recap_01' },
  { key: 'producer_quips.defensiveness_hard_cut_tears_01' },
  { key: 'producer_quips.defensiveness_qa_tickets_01' },
  { key: 'producer_quips.defensiveness_exposition_dump_01' },
  { key: 'producer_quips.defensiveness_unrequested_notes_01' },
  { key: 'producer_quips.defensiveness_live_buzzer_01' },
  { key: 'producer_quips.defensiveness_smoke_detector_01' },
  { key: 'producer_quips.defensiveness_punchup_accountability_01' },
  { key: 'producer_quips.defensiveness_4k_hdr_01' },
  { key: 'producer_quips.defensiveness_monday_morning_01' },
  { key: 'producer_quips.defensiveness_cross_examining_01' },
  { key: 'producer_quips.defensiveness_sloppy_wipe_01' },
  { key: 'producer_quips.defensiveness_infinite_mirrors_01' },
  { key: 'producer_quips.defensiveness_commas_fire_01' },
  { key: 'producer_quips.defensiveness_blaming_clapper_01' },
  { key: 'producer_quips.defensiveness_salt_michelin_01' },
  { key: 'producer_quips.defensiveness_waveform_spikes_01' },
  { key: 'producer_quips.defensiveness_last_season_villain_01' },
  { key: 'producer_quips.defensiveness_playback_clear_01' },
  { key: 'producer_quips.defensiveness_emotion_to_spec_01' },
  { key: 'producer_quips.defensiveness_zero_pages_01' },
  { key: 'producer_quips.defensiveness_recycling_broll_01' },
  { key: 'producer_quips.defensiveness_footage_villain_01' },
  { key: 'producer_quips.defensiveness_deal_memo_01' },
  { key: 'producer_quips.defensiveness_bible_annotations_01' },

  // Withdrawal (20) - Thousand-yard stare, dead air, mystery boxes, flatline waveforms
  { key: 'producer_quips.withdrawal_thousand_yard_01' },
  { key: 'producer_quips.withdrawal_grunts_missing_01' },
  { key: 'producer_quips.withdrawal_competing_screens_01' },
  { key: 'producer_quips.withdrawal_spontaneous_wrap_01' },
  { key: 'producer_quips.withdrawal_limbo_parking_01' },
  { key: 'producer_quips.withdrawal_lens_cap_01' },
  { key: 'producer_quips.withdrawal_cello_kazoo_01' },
  { key: 'producer_quips.withdrawal_dead_air_01' },
  { key: 'producer_quips.withdrawal_cold_open_01' },
  { key: 'producer_quips.withdrawal_crowd_scene_01' },
  { key: 'producer_quips.withdrawal_emergency_montage_01' },
  { key: 'producer_quips.withdrawal_calendar_invites_01' },
  { key: 'producer_quips.withdrawal_blackout_pages_01' },
  { key: 'producer_quips.withdrawal_kicking_can_01' },
  { key: 'producer_quips.withdrawal_props_disappearing_01' },
  { key: 'producer_quips.withdrawal_subtitles_faces_01' },
  { key: 'producer_quips.withdrawal_paper_cut_lockdown_01' },
  { key: 'producer_quips.withdrawal_b_roll_sunsets_01' },
  { key: 'producer_quips.withdrawal_marked_addressed_01' },
  { key: 'producer_quips.withdrawal_losing_light_noon_01' },
  { key: 'producer_quips.withdrawal_statue_challenge_01' },
  { key: 'producer_quips.withdrawal_product_placement_shiny_01' },
  { key: 'producer_quips.withdrawal_preroll_ad_01' },
  { key: 'producer_quips.withdrawal_mid_season_hiatuses_01' },
  { key: 'producer_quips.withdrawal_mystery_box_01' },
  { key: 'producer_quips.withdrawal_driving_montage_01' },
  { key: 'producer_quips.withdrawal_flatline_waveform_01' },
  { key: 'producer_quips.withdrawal_screen_test_empty_01' },
];
